import { useState, useMemo, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Lock, RotateCw, Crop, Maximize2, Columns2, Sparkles, CheckCircle2 } from "lucide-react";
import { useStudio, useLookups } from "@/store/StudioContext";
import { EmptyState, Notice, DemoBadge } from "@/components/common/States";
import MediaFrame from "@/components/common/MediaFrame";
import PendingDialog from "@/components/common/PendingDialog";
import { rotateImage, cropImage } from "@/lib/imageOps";
import { uploadAsset } from "@/lib/api";
import { isConnectionOperational } from "@/domain/connections";
import { isJobCompleted, isJobRunning, isJobFailed, getJobPrimaryAsset } from "@/domain/jobResults";
import { sceneAssets, sceneAssetIds } from "@/domain/assetUsage";
import {
  getReadableAssetTitle,
  getTechnicalFilename,
  formatItemTitle,
  getOriginalFileTooltip,
} from "@/domain/assetDisplay";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import { t } from "@/i18n";

const EditorView = ({ data }) => {
  const { state, dispatch, submitGeneration } = useStudio();
  const { assetById, referenceById } = useLookups();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { project, scenes, assets, selection } = data;
  const hasConnection = state.connections.length > 0;
  const hasOperational = state.connections.some(isConnectionOperational);

  const requestedAssetId = searchParams?.get?.("asset") || null;
  const requestedAsset = requestedAssetId ? assetById[requestedAssetId] : null;
  const isRequestedAssetInvalid = Boolean(
    requestedAssetId && (!requestedAsset || requestedAsset.projectId !== project.id)
  );

  // Escena candidata: escena seleccionada o primera del proyecto
  const currentScene = scenes.find((s) => s.id === selection.sceneId) || scenes[0] || null;

  // Resolución del activo a mostrar (imagen O vídeo) dentro del proyecto actual:
  // 1. Activo explícitamente solicitado por query param (?asset=) si pertenece a este proyecto
  // 2. Activo explícitamente seleccionado (selection.assetId) si pertenece a este proyecto
  // 3. Versión activa de la escena (selectedAssetId)
  // 4. Primer activo de la escena (imagen preferida)
  // 5. Primera imagen (o primer activo) del proyecto
  const asset = useMemo(() => {
    if (isRequestedAssetInvalid) return null;
    const inProject = (a) => Boolean(a) && a.projectId === project.id;
    if (requestedAsset && inProject(requestedAsset)) return requestedAsset;
    if (selection.assetId && inProject(assetById[selection.assetId])) return assetById[selection.assetId];
    if (currentScene) {
      if (inProject(assetById[currentScene.selectedAssetId])) return assetById[currentScene.selectedAssetId];
      const list = sceneAssets(currentScene, assets).filter(inProject);
      const first = list.find((a) => a.type === "image") || list[0];
      if (first) return first;
    }
    return assets.find((a) => a.type === "image") || assets[0] || null;
  }, [isRequestedAssetInvalid, requestedAsset, selection.assetId, currentScene, assetById, project.id, assets]);

  const isVideo = asset?.type === "video";
  // Escena a la que pertenece el activo mostrado (si no, la escena seleccionada).
  const scene = asset
    ? (scenes.find((s) => s.id === asset.sceneId) || scenes.find((s) => sceneAssetIds(s, assets).includes(asset.id)) || currentScene)
    : currentScene;
  const versions = scene ? sceneAssets(scene, assets) : [];

  const selectAsset = (targetAssetId, targetSceneId) => {
    if (typeof setSearchParams === "function") {
      setSearchParams({ asset: targetAssetId });
    }
    dispatch({
      type: "SELECT",
      payload: {
        projectId: project.id,
        patch: {
          assetId: targetAssetId,
          sceneId: targetSceneId || scene?.id || selection.sceneId,
        },
      },
    });
  };

  // Mantener sincronizados sceneId y assetId en el estado global sin recargar
  useEffect(() => {
    if (asset && (selection.assetId !== asset.id || (scene && selection.sceneId !== scene.id))) {
      dispatch({
        type: "SELECT",
        payload: {
          projectId: project.id,
          patch: {
            assetId: asset.id,
            sceneId: scene?.id || selection.sceneId,
          },
        },
      });
    }
  }, [asset, scene, selection.assetId, selection.sceneId, project.id, dispatch]);

  const original = asset ? assetById[asset.originalAssetId] || asset : null;
  const [instructions, setInstructions] = useState("");
  const [compare, setCompare] = useState(false);
  const [split, setSplit] = useState(50);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [pending, setPending] = useState(false);
  const [pendingJobId, setPendingJobId] = useState(null);
  const [completedJob, setCompletedJob] = useState(null);

  // Rastrear la imagen sobre la que se empezaron a redactar las instrucciones
  const initialInstructionAssetIdRef = useRef(null);
  useEffect(() => {
    if (instructions.trim() && !initialInstructionAssetIdRef.current) {
      initialInstructionAssetIdRef.current = asset?.id || null;
    } else if (!instructions.trim()) {
      initialInstructionAssetIdRef.current = null;
    }
  }, [instructions, asset?.id]);

  const baseChangedNotice = Boolean(
    instructions.trim() &&
    initialInstructionAssetIdRef.current &&
    asset?.id &&
    initialInstructionAssetIdRef.current !== asset.id
  );

  useEffect(() => {
    if (!pendingJobId) return;
    const j = state.jobs.find((job) => (job.backendJobId === pendingJobId || job.id === pendingJobId));
    if (!j) return;
    if (isJobCompleted(j)) {
      // Conservar la imagen base seleccionada en el editor; no desplazar la selección automáticamente
      setCompletedJob(j);
      setPendingJobId(null);
    } else if (isJobFailed(j)) {
      setError(j.errorDetail || t.generation?.failedToast || "La edición falló");
      setPendingJobId(null);
    }
  }, [pendingJobId, state.jobs]);

  if (isRequestedAssetInvalid) {
    return (
      <div data-testid="editor-view" className="flex min-h-0 flex-1 items-center justify-center p-8 bg-[#0d0d0d]">
        <div data-testid="editor-requested-asset-error" className="max-w-md w-full">
          <Notice tone="error">
            {t.editor?.requestedAssetNotFound || "El recurso solicitado no existe o no pertenece a este proyecto."}
          </Notice>
        </div>
      </div>
    );
  }

  if (!asset) {
    return (
      <div className="flex flex-1 items-center justify-center p-8">
        <EmptyState title={t.editor.empty} body={t.editor.emptyBody} testId="editor-empty" />
      </div>
    );
  }

  const completedResultAsset = completedJob ? getJobPrimaryAsset(completedJob, assetById) : null;
  const completedSourceAsset = completedJob?.source_asset_id ? assetById[completedJob.source_asset_id] : null;

  const history = assets.filter((a) => (a.originalAssetId || a.id) === (asset.originalAssetId || asset.id)).sort((a, b) => (a.createdAt || "").localeCompare(b.createdAt || ""));
  const refs = scene ? scene.referenceIds : [];

  // La rotación/recorte ocurre en un <canvas> local (sin proveedores de IA).
  // El resultado se sube al almacenamiento protegido como un archivo nuevo
  // -ligado al original vía originalAssetId/parentAssetId- en vez de guardar
  // el data URL embebido en el documento de estado.
  const runLocal = async (op, label) => {
    setBusy(true); setError(null);
    try {
      const out = await op(asset.url);
      const blob = await (await fetch(out.url)).blob();
      const uploaded = await uploadAsset(blob, `${(asset.label || "edit").replace(/\s+/g, "-")}-${label}.jpg`);
      dispatch({
        type: "ASSET_ADD",
        payload: {
          id: uploaded.id,
          url: uploaded.url,
          width: out.width,
          height: out.height,
          projectId: project.id,
          sceneId: asset.sceneId || scene?.id || null,
          type: "image",
          source: "edit",
          label: `${(asset.label || t.common.untitled).replace(/ · .*$/, "")} · ${label}`,
          originalAssetId: asset.originalAssetId || asset.id,
          parentAssetId: asset.id,
          isDemo: false,
        },
      });
    } catch {
      setError(t.editor.error);
    } finally {
      setBusy(false);
    }
  };

  const applyInstructions = async () => {
    if (!hasOperational) return;
    const promptText = instructions.trim();
    if (!promptText) return;
    if (!asset || !asset.id) return;

    // Captura fija de la base seleccionada al confirmar; cambios posteriores de selección
    // no alteran el trabajo ya capturado y enviado
    const baseAssetSnapshot = asset;
    const baseAssetId = baseAssetSnapshot.id;
    const baseSceneId = baseAssetSnapshot.sceneId || scene?.id || null;

    try {
      setBusy(true);
      setError(null);
      if (submitGeneration) {
        const activeConn = state.connections.find(isConnectionOperational) || state.connections[0];
        const job = await submitGeneration({
          provider_id: activeConn?.provider || "apimart",
          model_id: "seedream-5-0-pro",
          kind: "edit",
          prompt: promptText,
          source_asset_id: baseAssetId,
          reference_asset_ids: refs.map((rId) => referenceById[rId]?.assetId).filter(Boolean),
          project_id: project.id,
          scene_id: baseSceneId,
          params: { aspect_ratio: "auto" },
        });
        setPendingJobId(job.job_id || null);
        // Conservar las instrucciones en el textarea tras enviar
      } else {
        dispatch({
          type: "JOB_CREATE",
          payload: {
            kind: "edit",
            projectId: project.id,
            sceneId: baseSceneId,
            prompt: promptText,
            params: { sourceAssetId: baseAssetId },
            referenceIds: refs,
            status: "blocked",
            note: t.tasks.blockedReasonNoKey,
          },
        });
        setPending(true);
      }
    } catch (err) {
      setError(err?.message || t.editor.error || "Error al enviar la edición");
      // Conservar las instrucciones ante error
    } finally {
      setBusy(false);
    }
  };

  const mainTitle = getReadableAssetTitle({ asset, scene, t });
  const technicalFilename = getTechnicalFilename(asset);
  const originalFileTooltip = getOriginalFileTooltip({ asset, t });

  return (
    <div data-testid="editor-view" className="flex min-h-0 flex-1 flex-col overflow-hidden lg:flex-row">
      <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden bg-[#0d0d0d] p-3 md:p-5">
        <div className="mb-2 flex shrink-0 flex-wrap items-center gap-2 text-xs text-[#a3a3a3]">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span
                  data-testid="editor-asset-title"
                  className="max-w-[280px] sm:max-w-[380px] md:max-w-[500px] truncate font-medium text-[#d8d4cd] cursor-default"
                  title={mainTitle}
                >
                  {mainTitle}
                </span>
              </TooltipTrigger>
              <TooltipContent side="bottom" align="start" className="max-w-xs bg-[#1a1a1a] text-[#d8d4cd] border border-[#333]">
                <span className="font-mono text-xs text-[#f4f1ec] break-all">{originalFileTooltip}</span>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          {asset.isDemo && <DemoBadge />}
          <span className="font-mono">{asset.width && asset.height ? `${asset.width}×${asset.height}` : ""}</span>
          {!isVideo && <button data-testid="editor-compare-toggle" onClick={() => setCompare((c) => !c)} disabled={original.id === asset.id} className={`ml-auto v-btn-ghost v-btn-sm ${compare ? "border-[#f5b544] text-[#f5b544]" : ""}`}><Columns2 size={12} />{t.editor.compare}</button>}
        </div>
        <div className="relative flex min-h-0 flex-1 items-center justify-center overflow-hidden">
          {isVideo ? (
            <MediaFrame key={asset.id} asset={asset} controls className="flex h-full w-full max-h-full max-w-full items-center justify-center border-0 bg-transparent" fit="contain" testId="editor-main-video" />
          ) : compare && original.id !== asset.id ? (
            <div data-testid="editor-compare" className="relative max-h-full max-w-full overflow-hidden rounded-lg border border-[#262626]" style={{ aspectRatio: `${asset.width || 16} / ${asset.height || 9}` }}>
              <img src={asset.url} alt={t.editor.after} className="absolute inset-0 h-full w-full object-contain" draggable={false} />
              <div className="absolute inset-0 overflow-hidden" style={{ width: `${split}%` }}><img src={original.url} alt={t.editor.before} className="absolute inset-0 h-full w-full object-contain" style={{ width: `${10000 / split}%`, maxWidth: "none" }} draggable={false} /></div>
              <div className="absolute inset-y-0 w-px bg-[#f5b544]" style={{ left: `${split}%` }} />
              <span className="absolute left-2 top-2 rounded bg-[#101010]/80 px-1.5 py-0.5 text-[10px]">{t.editor.before}</span>
              <span className="absolute right-2 top-2 rounded bg-[#101010]/80 px-1.5 py-0.5 text-[10px]">{t.editor.after}</span>
              <input data-testid="editor-compare-slider" type="range" min="1" max="99" value={split} onChange={(e) => setSplit(Number(e.target.value))} className="absolute bottom-3 left-1/2 w-1/2 -translate-x-1/2 accent-[#f5b544]" />
            </div>
          ) : (
            <MediaFrame key={asset.id} asset={asset} className="flex h-full w-full max-h-full max-w-full items-center justify-center border-0 bg-transparent" fit="contain" testId="editor-main-image" />
          )}
          {busy && <div data-testid="editor-busy" className="absolute inset-0 flex items-center justify-center bg-[#0d0d0d]/70 text-sm">{t.editor.processing}</div>}
        </div>
        {error && <Notice tone="warn" testId="editor-error" className="mt-2 shrink-0">{error}</Notice>}
      </div>

      <aside data-testid="editor-sidebar" className="flex shrink-0 flex-col gap-5 overflow-y-auto border-t border-[#303030] bg-[#161616] p-4 lg:w-[360px] lg:border-l lg:border-t-0">
        {versions.length > 0 && (
          <div data-testid="editor-scene-versions">
            <div className="mb-1.5 text-xs font-medium text-[#a3a3a3]">
              {t.editor.sceneVersions || "Versiones de la escena"} ({versions.length})
            </div>
            <ul className="flex flex-col gap-1.5">
              {versions.map((a) => {
                const inUse = scene?.selectedAssetId === a.id;
                const isSelected = a.id === asset.id;
                const readableName = getReadableAssetTitle({ asset: a, scene, t });
                const fileType = a.type === "video" ? (t.editor.videoType || "Vídeo") : (t.editor.imageType || "Imagen");
                const itemTitle = formatItemTitle(readableName, fileType);
                const techTooltip = getOriginalFileTooltip({ asset: a, t });
                const showFileTypeSubtitle = !readableName.toLowerCase().includes(fileType.toLowerCase());
                return (
                  <li
                    key={a.id}
                    className={`flex items-center gap-2 rounded-md border p-1.5 text-xs transition-colors ${
                      isSelected
                        ? "border-[#f5b544] bg-[#1e1808]/90 ring-1 ring-[#f5b544]/30"
                        : "border-[#303030] hover:bg-[#1b1b1b]"
                    }`}
                  >
                    <button
                      type="button"
                      data-testid={`editor-version-view-${a.id}`}
                      title={itemTitle}
                      onClick={() => selectAsset(a.id, scene?.id)}
                      className="flex min-w-0 flex-1 items-center gap-2 text-left"
                    >
                      <div
                        className="relative h-10 w-10 shrink-0 overflow-hidden rounded-sm bg-[#121212]"
                        title={techTooltip}
                      >
                        <MediaFrame asset={a} className="h-full w-full rounded-none border-0" showBadge={false} fit="cover" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="truncate font-medium text-[#f4f1ec]" title={readableName}>
                          {a.type === "video" ? "▶ " : ""}{readableName}
                        </div>
                        <div className="flex items-center gap-1.5 font-mono text-[10px] text-[#8c8c8c]">
                          {showFileTypeSubtitle && <span>{fileType}</span>}
                          {a.width && a.height ? <span>{showFileTypeSubtitle ? "· " : ""}{a.width}×{a.height}</span> : null}
                        </div>
                      </div>
                    </button>

                    <div className="flex shrink-0 items-center gap-1.5">
                      {isSelected && (
                        <span
                          data-testid={`editor-version-selected-${a.id}`}
                          className="shrink-0 rounded border border-[#f5b544]/50 bg-[#f5b544]/20 px-1.5 py-0.5 font-mono text-[10px] font-semibold text-[#f5b544]"
                        >
                          {t.editor.selectedVersion || "Seleccionada"}
                        </span>
                      )}
                      {inUse ? (
                        <span
                          data-testid={`editor-version-inuse-${a.id}`}
                          className="shrink-0 rounded bg-[#f5b544]/15 px-1.5 py-0.5 text-[10px] font-medium text-[#f5b544]"
                        >
                          {t.editor.inUse || "En uso"}
                        </span>
                      ) : (
                        <button
                          type="button"
                          data-testid={`editor-version-use-${a.id}`}
                          title="Usar esta versión en la escena"
                          onClick={() =>
                            scene &&
                            dispatch({
                              type: "SCENE_SELECT_ASSET",
                              payload: { sceneId: scene.id, assetId: a.id },
                            })
                          }
                          className="v-btn-ghost v-btn-sm shrink-0"
                        >
                          {t.editor.useInScene || "Usar"}
                        </button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {/* Tarjeta de resultado completado e imagen de origen */}
        {completedResultAsset && (
          <div data-testid="editor-completed-result" className="rounded-md border border-[#2d4a2d] bg-[#142314] p-3 text-xs">
            <div className="flex items-center justify-between font-semibold text-[#86efac] mb-2">
              <span className="flex items-center gap-1.5">
                <CheckCircle2 size={13} className="text-[#4ade80]" />
                {t.editor.lastResultTitle || "Último resultado completado"}
              </span>
              <span className="text-[10px] uppercase font-mono px-1.5 py-0.5 rounded bg-[#1f3f1f] text-[#4ade80]">OK</span>
            </div>
            <div className="flex items-center gap-2.5 mb-2.5">
              <div className="h-10 w-10 shrink-0 overflow-hidden rounded border border-[#2d4a2d] bg-black">
                <MediaFrame asset={completedResultAsset} className="h-full w-full rounded-none border-0" showBadge={false} fit="cover" />
              </div>
              <div className="min-w-0 flex-1">
                <div data-testid="editor-completed-result-name" className="truncate font-medium text-[#f4f1ec]">
                  {getReadableAssetTitle({ asset: completedResultAsset, scene, t })}
                </div>
                <div data-testid="editor-completed-origin-name" className="text-[10px] text-[#a3a3a3] truncate">
                  {t.editor.generatedFrom || "Generado a partir de"}: {completedSourceAsset ? getReadableAssetTitle({ asset: completedSourceAsset, scene, t }) : (completedJob?.source_asset_id || "—")}
                </div>
              </div>
            </div>
            <button
              type="button"
              data-testid="editor-view-result-btn"
              onClick={() => selectAsset(completedResultAsset.id, completedResultAsset.sceneId)}
              className="v-btn-ghost v-btn-sm w-full justify-center text-[#86efac] border-[#2d4a2d] hover:bg-[#1f3f1f]"
            >
              <Sparkles size={12} />
              {t.editor.viewResult || "Ver resultado en el editor"}
            </button>
          </div>
        )}

        {isVideo ? (
          <p data-testid="editor-video-note" className="text-[11px] text-[#a3a3a3]">
            {t.editor.videoNote}
          </p>
        ) : (<>
        <div>
          {/* Tarjeta de imagen que vas a editar */}
          <div data-testid="editor-base-asset-card" className="mb-3 rounded-md border border-[#303030] bg-[#121212] p-2.5">
            <div className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-[#a3a3a3]">
              {t.editor.imageToEdit || "Imagen que vas a editar"}
            </div>
            <div className="flex items-center gap-2.5">
              <div className="relative h-11 w-11 shrink-0 overflow-hidden rounded border border-[#3a3a3a] bg-black">
                <MediaFrame asset={asset} className="h-full w-full rounded-none border-0" showBadge={false} fit="cover" />
              </div>
              <div className="min-w-0 flex-1">
                <div data-testid="editor-base-asset-name" className="truncate font-medium text-xs text-[#f4f1ec]" title={mainTitle}>
                  {mainTitle}
                </div>
                <div data-testid="editor-base-asset-id" className="font-mono text-[10px] text-[#8c8c8c] truncate">
                  ID: {asset.id}
                </div>
              </div>
            </div>
            {baseChangedNotice && (
              <div data-testid="editor-base-changed-notice" className="mt-2 rounded bg-[#f5b544]/15 border border-[#f5b544]/40 px-2 py-1 text-[11px] text-[#f5b544]">
                {t.editor.baseImageChanged || "La imagen base ha cambiado a esta versión. Las instrucciones se aplicarán sobre ella."}
              </div>
            )}
          </div>

          <div className="mb-1.5 text-xs font-medium text-[#a3a3a3]">{t.editor.instructions}</div>
          <textarea data-testid="editor-instructions" className="v-textarea" rows={4} placeholder={t.editor.instructionsPh} value={instructions} onChange={(e) => setInstructions(e.target.value)} />
          <div className="mt-2 flex flex-wrap gap-1.5">
            {refs.map((id) => <span key={id} data-testid={`editor-ref-${id}`} className="rounded border border-[#303030] px-1.5 py-0.5 font-mono text-[10px] text-[#a3a3a3]">{referenceById[id]?.label || id}</span>)}
          </div>
          <button data-testid="editor-apply-instructions" className="v-btn-primary v-btn-sm mt-2 w-full justify-center" disabled={!instructions.trim() || !hasOperational} title={!hasOperational ? (hasConnection ? t.connections.noneOperational : t.connections.required) : undefined} onClick={applyInstructions}><Lock size={12} />{t.editor.applyInstructions}</button>
          {!hasOperational && <Notice tone="warn" testId="editor-connection-notice">{hasConnection ? t.connections.noneOperational : t.connections.required}</Notice>}
        </div>

        <div>
          <div className="mb-1.5 text-xs font-medium text-[#a3a3a3]">{t.editor.localOp}</div>
          <div className="grid grid-cols-2 gap-1.5">
            <button data-testid="editor-rotate" className="v-btn-ghost v-btn-sm justify-center" disabled={busy} onClick={() => runLocal(rotateImage, "rot90")}><RotateCw size={12} />{t.editor.rotate}</button>
            <button data-testid="editor-upscale" className="v-btn-ghost v-btn-sm justify-center" onClick={() => navigate(`/tools/upscale?asset=${asset.id}&project=${project.id}`)}><Maximize2 size={12} />{t.editor.sendUpscale}</button>
          </div>
          <div className="mt-1.5 grid grid-cols-4 gap-1.5">
            {Object.keys(t.editor.cropRatios).map((r) => <button key={r} data-testid={`editor-crop-${r.replace(":", "x")}`} className="v-btn-ghost v-btn-sm justify-center" disabled={busy} onClick={() => runLocal((u) => cropImage(u, r), `crop ${r}`)}><Crop size={11} />{r}</button>)}
          </div>
          <p className="mt-2 text-[11px] text-[#a3a3a3]">{t.editor.originalNote}</p>
        </div>
        </>)}

        <div>
          <div className="mb-1.5 text-xs font-medium text-[#a3a3a3]">
            {t.editor.history} ({history.length})
          </div>
          <ul className="flex flex-col gap-1.5" data-testid="editor-history">
            {history.map((a, i) => {
              const isSelected = a.id === asset.id;
              const versionPrefix = i === 0 ? t.common.original : `v${i + 1}`;
              const readableName = getReadableAssetTitle({ asset: a, scene, t });
              const label = `${versionPrefix} · ${readableName}`;
              const techTooltip = getOriginalFileTooltip({ asset: a, t });
              const fileType = a.type === "video" ? (t.editor.videoType || "Vídeo") : (t.editor.imageType || "Imagen");
              const itemTitle = formatItemTitle(label, fileType);
              const showFileTypeSubtitle = !readableName.toLowerCase().includes(fileType.toLowerCase());
              return (
                <li key={a.id}>
                  <button
                    type="button"
                    data-testid={`editor-history-${a.id}`}
                    title={itemTitle}
                    onClick={() => selectAsset(a.id, a.sceneId)}
                    className={`flex w-full items-center gap-2 rounded-md border p-1.5 text-left text-xs transition-colors ${
                      isSelected
                        ? "border-[#f5b544] bg-[#1e1808]/90 ring-1 ring-[#f5b544]/30"
                        : "border-[#303030] hover:bg-[#1b1b1b]"
                    }`}
                  >
                    <div
                      className="relative h-10 w-10 shrink-0 overflow-hidden rounded-sm bg-[#121212]"
                      title={techTooltip}
                    >
                      <MediaFrame asset={a} className="h-full w-full rounded-none border-0" showBadge={false} fit="cover" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="truncate font-medium text-[#f4f1ec]" title={label}>
                        {label}
                      </div>
                      <div className="flex items-center gap-1.5 font-mono text-[10px] text-[#8c8c8c]">
                        {showFileTypeSubtitle && <span>{fileType}</span>}
                        {a.source ? <span>{showFileTypeSubtitle ? "· " : ""}{t.gallery.source[a.source] || a.source}</span> : null}
                      </div>
                    </div>
                    {isSelected && (
                      <span className="shrink-0 rounded border border-[#f5b544]/50 bg-[#f5b544]/20 px-1.5 py-0.5 font-mono text-[10px] font-semibold text-[#f5b544]">
                        {t.editor.selectedVersion || "Seleccionada"}
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      </aside>
      <PendingDialog open={pending} onOpenChange={setPending} phase={3} registered />
    </div>
  );
};

export default EditorView;
